import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getApiClient } from '@/lib/api'
import type { BenchmarkCurationAnnotationStatus, BenchmarkCurationPacketUpdateRequest } from '@/types/api'

const CURATION_KEYS = {
  all: ['benchmark-curation'] as const,
  overview: () => [...CURATION_KEYS.all, 'overview'] as const,
  queue: (limit: number, includeClosed: boolean) =>
    [...CURATION_KEYS.all, 'queue', limit, includeClosed] as const,
  packets: (params: { status: string; q: string; limit: number; offset: number }) =>
    [...CURATION_KEYS.all, 'packets', params] as const,
  packet: (packetId: string) => [...CURATION_KEYS.all, 'packet', packetId] as const,
}

export function useBenchmarkCurationOverview() {
  return useQuery({
    queryKey: CURATION_KEYS.overview(),
    queryFn: () => getApiClient().getBenchmarkCurationOverview(),
    staleTime: 1000 * 60,
  })
}

export function useBenchmarkCurationQueue(limit = 20, includeClosed = false) {
  return useQuery({
    queryKey: CURATION_KEYS.queue(limit, includeClosed),
    queryFn: () => getApiClient().getBenchmarkCurationQueue({ limit, includeClosed }),
    staleTime: 1000 * 60,
  })
}

export function useBenchmarkCurationPackets(params: {
  status: BenchmarkCurationAnnotationStatus | 'all'
  q: string
  limit: number
  offset: number
}) {
  return useQuery({
    queryKey: CURATION_KEYS.packets(params),
    queryFn: () => getApiClient().listBenchmarkCurationPackets(params),
    staleTime: 1000 * 30,
  })
}

export function useBenchmarkCurationPacket(packetId: string) {
  return useQuery({
    queryKey: CURATION_KEYS.packet(packetId),
    queryFn: () => getApiClient().getBenchmarkCurationPacket(packetId),
    enabled: packetId.length > 0,
    staleTime: 1000 * 30,
  })
}

export function useUpdateBenchmarkCurationPacket(packetId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: BenchmarkCurationPacketUpdateRequest) =>
      getApiClient().updateBenchmarkCurationPacket(packetId, input),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: CURATION_KEYS.overview() }),
        queryClient.invalidateQueries({ queryKey: CURATION_KEYS.all }),
      ])
    },
  })
}

